import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';

import VM from 'scratch-vm';
import AudioEngine from 'scratch-audio';

import {setProjectUnchanged} from '../reducers/project-changed';
import {
    LoadingStates,
    getIsLoadingWithId,
    onLoadedProject,
    projectError
} from '../reducers/project-state';

/*
 * Higher Order Component to manage events emitted by the VM
 * @param {React.Component} WrappedComponent component to manage VM events for
 * @returns {React.Component} connected component with vm events bound to redux
 */
const vmManagerHOC = function (WrappedComponent) {
    class VMManager extends React.Component {
        constructor (props) {
            super(props);
            bindAll(this, [
                'loadProject',
                'installStageBoundaryPrimitive',
                'installStageBoundaryMotionWrappers',
                'applyStageBoundaryMode',
                'daysSinceDate',
                'turn',
                'costume',
                'operators'
            ]);
        }

        // ===== STAGE BOUNDARY SYSTEM =====

        applyStageBoundaryMode (util, mode) {
            if (!util || !util.target) {
                return;
            }

            const vm = this.props.vm;
            const stageWidth = vm.runtime.constructor.STAGE_WIDTH;
            const stageHeight = vm.runtime.constructor.STAGE_HEIGHT;
            const halfStageWidth = stageWidth / 2;
            const halfStageHeight = stageHeight / 2;
            const maxInfiniteCoordinate = 999;

            let {x, y} = util.target;
            if (mode === 'loop') {
                while (x < -halfStageWidth) x += stageWidth;
                while (x > halfStageWidth) x -= stageWidth;
                while (y < -halfStageHeight) y += stageHeight;
                while (y > halfStageHeight) y -= stageHeight;
            } else if (mode === 'infinite') {
                if (x < -maxInfiniteCoordinate) x = -maxInfiniteCoordinate;
                if (x > maxInfiniteCoordinate) x = maxInfiniteCoordinate;
                if (y < -maxInfiniteCoordinate) y = -maxInfiniteCoordinate;
                if (y > maxInfiniteCoordinate) y = maxInfiniteCoordinate;
            } else {
                if (x < -halfStageWidth) x = -halfStageWidth;
                if (x > halfStageWidth) x = halfStageWidth;
                if (y < -halfStageHeight) y = -halfStageHeight;
                if (y > halfStageHeight) y = halfStageHeight;
            }

            if (x !== util.target.x || y !== util.target.y) {
                util.target.setXY(x, y);
            }
        }

        installStageBoundaryMotionWrappers () {
            const vm = this.props.vm;
            if (!vm.runtime || !vm.runtime._primitives || vm.runtime.stageBoundaryMotionWrappersInstalled) {
                return;
            }
            vm.runtime._primitives.turn = this.turn.bind(this);
            const motionPrimitives = [
                'motion_movesteps',
                'motion_changexby',
                'motion_setx',
                'motion_changeyby',
                'motion_sety',
                'motion_gotoxy',
                'motion_glideto',
                'motion_glidesecstoxy'
            ];

            vm.runtime.stageBoundaryMotionWrappersInstalled = true;
            vm.runtime._stageBoundaryOriginalPrimitives = vm.runtime._stageBoundaryOriginalPrimitives || {};

            motionPrimitives.forEach(name => {
                const originalPrimitive = vm.runtime._primitives[name];
                if (typeof originalPrimitive !== 'function' || vm.runtime._stageBoundaryOriginalPrimitives[name]) {
                    return;
                }

                vm.runtime._stageBoundaryOriginalPrimitives[name] = originalPrimitive;
                vm.runtime._primitives[name] = (args, util) => {
                    const target = util && util.target;
                    const originalKeepInFence = target && target.keepInFence;
                    const mode = vm.runtime.stageBoundaryMode || 'stop';

                    if (target && originalKeepInFence) {
                        target.keepInFence = (newX, newY) => [newX, newY];
                    }

                    try {
                        const result = originalPrimitive(args, util);
                        this.applyStageBoundaryMode(util, mode);
                        return result;
                    } finally {
                        if (target && originalKeepInFence) {
                            target.keepInFence = originalKeepInFence;
                        }
                    }
                };
            });
        }

        installStageBoundaryPrimitive () {
            const vm = this.props.vm;
            if (!vm.runtime || !vm.runtime._primitives) {
                return;
            }

            if (vm.runtime.stageBoundaryMode === undefined) {
                vm.runtime.stageBoundaryMode = 'stop';
            }

            this.installStageBoundaryMotionWrappers();

            if (vm.runtime._primitives.motion_stage_boundary) {
                return;
            }

            vm.runtime._primitives.motion_stage_boundary = (args) => {
                const mode = args.MODE || vm.runtime.stageBoundaryMode || 'stop';
                vm.runtime.stageBoundaryMode = mode;
            };
        }

        daysSinceDate (args, util) {
            const year = Number(args.YEAR);
            const msPerDay = 24 * 60 * 60 * 1000;
            const start = new Date(year, 0, 1);
            const today = new Date();
            const dstAdjust = today.getTimezoneOffset() - start.getTimezoneOffset();
            let mSecsSinceStart = today.valueOf() - start.valueOf();
            mSecsSinceStart += ((today.getTimezoneOffset() - dstAdjust) * 60 * 1000);
            return mSecsSinceStart / msPerDay;
        }

        // ===== TURN =====

        turn (args, util) {
            const degrees = Number(args.DEGREES);
            util.target.setDirection(util.target.direction + degrees);
        }

        // ===== COSTUME SYSTEM =====

        costume (args, util) {
            const target = util.target;
            const value = args.TARGET;
            if (value === 'NEXT') {
                target.setCostume(target.currentCostume + 1);
            } else if (value === 'PREV') {
                target.setCostume(target.currentCostume - 1);
            } else {
                const costumes = target.getCostumes();
                const index = costumes.findIndex(c => c.name === value);
                if (index >= 0) {
                    target.setCostume(index);
                }
            }
        }

        // ===== OPERATORS =====

        operators (args) {
            const a = Number(args.OPERAND1);
            const b = Number(args.OPERAND2);
            const op = args.OPERAND;
            if (op === 'EQUAL') {
                return a === b ||
                    String(args.OPERAND1).toLowerCase() === String(args.OPERAND2).toLowerCase();
            } else if (op === 'LESS') {
                return a < b;
            } else {
                return a > b;
            }
        }

        // ===== LIFECYCLE =====

        componentDidMount () {
            if (!this.props.vm.initialized) {
                this.audioEngine = new AudioEngine();
                this.props.vm.attachAudioEngine(this.audioEngine);
                this.props.vm.setCompatibilityMode(true);
                this.props.vm.initialized = true;
                this.props.vm.setLocale(this.props.locale, this.props.messages);
            }
            this.installStageBoundaryPrimitive();

            const vm = this.props.vm;
            if (vm.runtime && vm.runtime._primitives) {
                vm.runtime._primitives.costume = this.costume.bind(this);
                vm.runtime._primitives.operators = this.operators.bind(this);
                vm.runtime._primitives.daysSinceDate = this.daysSinceDate.bind(this);
            }

            if (!this.props.isPlayerOnly && !this.props.isStarted) {
                this.props.vm.start();
            }
        }

        componentDidUpdate (prevProps) {
            if (this.props.isLoadingWithId && this.props.fontsLoaded &&
                (!prevProps.isLoadingWithId || !prevProps.fontsLoaded)) {
                this.loadProject();
            }
            if (!this.props.isPlayerOnly && !this.props.isStarted) {
                this.props.vm.start();
            }
        }

        loadProject () {
            return this.props.vm.loadProject(this.props.projectData)
                .then(() => {
                    this.props.onLoadedProject(this.props.loadingState, this.props.canSave);
                    setTimeout(() => this.props.onSetProjectUnchanged());
                    if (!this.props.isStarted) {
                        setTimeout(() => this.props.vm.renderer.draw());
                    }
                })
                .catch(e => {
                    this.props.onError(e);
                });
        }

        render () {
            const {
                /* eslint-disable no-unused-vars */
                fontsLoaded,
                loadingState,
                locale,
                messages,
                isStarted,
                onError: onErrorProp,
                onLoadedProject: onLoadedProjectProp,
                onSetProjectUnchanged,
                projectData,
                /* eslint-enable no-unused-vars */
                isLoadingWithId: isLoadingWithIdProp,
                vm,
                ...componentProps
            } = this.props;
            return (
                <WrappedComponent
                    isLoading={isLoadingWithIdProp}
                    vm={vm}
                    {...componentProps}
                />
            );
        }
    }

    VMManager.propTypes = {
        canSave: PropTypes.bool,
        cloudHost: PropTypes.string,
        fontsLoaded: PropTypes.bool,
        isLoadingWithId: PropTypes.bool,
        isPlayerOnly: PropTypes.bool,
        isStarted: PropTypes.bool,
        loadingState: PropTypes.oneOf(LoadingStates),
        locale: PropTypes.string,
        messages: PropTypes.objectOf(PropTypes.string),
        onError: PropTypes.func,
        onLoadedProject: PropTypes.func,
        onSetProjectUnchanged: PropTypes.func,
        projectData: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        username: PropTypes.string,
        vm: PropTypes.instanceOf(VM).isRequired
    };

    const mapStateToProps = state => {
        const loadingState = state.bitByteGui.projectState.loadingState;
        return {
            fontsLoaded: state.bitByteGui.fontsLoaded,
            isLoadingWithId: getIsLoadingWithId(loadingState),
            locale: state.locales.locale,
            messages: state.locales.messages,
            projectData: state.bitByteGui.projectState.projectData,
            projectId: state.bitByteGui.projectState.projectId,
            loadingState: loadingState,
            isPlayerOnly: state.bitByteGui.mode.isPlayerOnly,
            isStarted: state.bitByteGui.vmStatus.started
        };
    };

    const mapDispatchToProps = dispatch => ({
        onError: error => dispatch(projectError(error)),
        onLoadedProject: (loadingState, canSave) =>
            dispatch(onLoadedProject(loadingState, canSave, true)),
        onSetProjectUnchanged: () => dispatch(setProjectUnchanged())
    });

    const mergeProps = (stateProps, dispatchProps, ownProps) => Object.assign(
        {}, stateProps, dispatchProps, ownProps
    );

    return connect(
        mapStateToProps,
        mapDispatchToProps,
        mergeProps
    )(VMManager);
};

export default vmManagerHOC;
